// src/api/snowIceRemovalLog.js
import axios from "../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Snow/Ice Removal Log operations (v2)
 */
export const SNOW_ICE_ENDPOINTS = {
    CREATE_LOG: "v2/snow-ice-removal-logs",
    GET_ALL_LOGS: "v2/snow-ice-removal-logs",
    GET_LOG_BY_ID: "v2/snow-ice-removal-logs/:id",
    APPROVE_LOG: "v2/snow-ice-removal-logs/:id/approve",
    REJECT_LOG: "v2/snow-ice-removal-logs/:id/reject",
    GET_SIGNED_URL: "v2/snow-ice-removal-logs/signed-url",
    UPLOAD_PHOTOS: "v2/snow-ice-removal-logs/:propertyId/upload-photos",
};

/**
 * Centralized error handler
 */
const handleApiError = (error, defaultMessage) => {
    const message = error.response?.data?.message || error.message || defaultMessage;
    console.error(defaultMessage, error);
    toast.error(message);
    throw error; // Re-throw to allow caller to handle if needed
};

/**
 * Create a new Snow/Ice Removal Log
 */
export const createSnowIceRemovalLog = async (payload) => {
    try {
        const response = await axios.post(SNOW_ICE_ENDPOINTS.CREATE_LOG, payload, {
            headers: { 'Content-Type': 'application/json' },
        });
        toast.success(response.data.message || 'Snow/Ice removal log submitted successfully');
        return response.data;
    } catch (error) {
        handleApiError(error, "Failed to create snow/ice removal log");
    }
};

/**
 * Fetch all Snow/Ice Removal Logs
 */
export const getAllSnowIceRemovalLogs = async (params = {}) => {
    try {
        const response = await axios.get(SNOW_ICE_ENDPOINTS.GET_ALL_LOGS, { params });
        return response.data;
    } catch (error) {
        handleApiError(error, "Failed to fetch snow/ice removal logs");
    }
};

/**
 * Fetch a single log by ID
 */
export const getSnowIceRemovalLogById = async (id) => {
    try {
        const url = SNOW_ICE_ENDPOINTS.GET_LOG_BY_ID.replace(':id', id);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        handleApiError(error, "Failed to fetch snow/ice removal log");
    }
};

/**
 * Approve a log
 */
export const approveSnowIceRemovalLog = async (id, payload = { comments: '' }) => {
    try {
        const url = SNOW_ICE_ENDPOINTS.APPROVE_LOG.replace(':id', id);
        const response = await axios.patch(url, payload);
        toast.success(response.data.message || 'Log approved successfully');
        return response.data;
    } catch (error) {
        handleApiError(error, "Failed to approve snow/ice removal log");
    }
};

/**
 * Reject a log (requires comments)
 */
export const rejectSnowIceRemovalLog = async (id, comments) => {
    try {
        if (!comments || comments.trim() === '') {
            toast.error('Comments are required when rejecting a log');
            throw new Error('Comments are required');
        }

        const url = SNOW_ICE_ENDPOINTS.REJECT_LOG.replace(':id', id);
        const response = await axios.patch(url, { comments });
        toast.success(response.data.message || 'Log rejected');
        return response.data;
    } catch (error) {
        handleApiError(error, "Failed to reject snow/ice removal log");
    }
};

/**
 * Generate a signed URL for viewing or uploading an image
 * @param {string} key - S3 key (e.g. snow-ice-removal/abc123/1234567890.jpg)
 * @param {'get'|'put'} operation - 'get' for preview, 'put' for upload
 * @param {string} type - File type: jpg|png|webp
 */
export const getSignedUrl = async (key, operation = 'get', type = 'jpg') => {
    try {
        const response = await axios.get(SNOW_ICE_ENDPOINTS.GET_SIGNED_URL, {
            params: { key: encodeURIComponent(key), operation, type },
        });
        return response.data.data; // { signedUrl, key }
    } catch (error) {
        handleApiError(error, "Failed to generate signed URL");
    }
};

/**
 * Upload photos (before/after) for a property
 * @param {string} propertyId - Property ObjectId
 * @param {File[]} files - Array of File objects from input
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Array>} Array of uploaded photo metadata
 */
export const uploadPhotos = async (propertyId, files, onProgress = null) => {
    if (!files || files.length === 0) {
        toast.warn("No photos selected");
        return [];
    }

    const formData = new FormData();
    files.forEach(file => formData.append('photos', file));

    try {
        const response = await axios.post(
            SNOW_ICE_ENDPOINTS.UPLOAD_PHOTOS.replace(':propertyId', propertyId),
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: onProgress ? (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percent);
                } : undefined,
            }
        );

        toast.success(response.data.message || `${files.length} photo(s) uploaded successfully`);
        return response.data.data; // Array of { fileUrl, fileKey, ... }
    } catch (error) {
        handleApiError(error, "Failed to upload photos");
        return [];
    }
};